<script setup lang="ts">
import {
  type MapMarker,
  MapMarkerLayer,
  MapPopupRenderer,
  useMap,
  renderMarkerWithImage,
} from '@glow/shared/lib/ol';
import PartnerLocations from './PartnerLocations.vue';
import PartnerLocation from './PartnerLocation.vue';
import { type PartnerLocationData } from './PartnerLocation.vue';
import type { Coords } from '@glow/shared/lib/coords';
import { useVModel } from '@vueuse/core';
import { PlaceholderableImg } from '@glow/shared/components/misc';

export type MapPartnerLocation = {
  location: Coords;
  photoUrls: string[];
} & PartnerLocationData;

const props = withDefaults(
  defineProps<{
    locations: MapPartnerLocation[];
    locationIndex?: number;
  }>(),
  { locationIndex: undefined }
);
defineEmits<{
  (e: 'update:locationIndex', locationIndex: number): void;
}>();

const locationIndex = useVModel(props, 'locationIndex');
const { staticUrl } = useRuntimeConfig().public;

const popupElement = ref<HTMLDivElement>();
const currentLocation = computed(
  () => props.locations[locationIndex.value ?? 0]
);
const isLightboxVisible = ref(false);
const mapElementRef = ref<HTMLElement>();

function selectLocation(index: number) {
  locationIndex.value = index;
  popupLocationIndex.value = index;
}

const popupLocationIndex = ref<number>();
watch(
  popupLocationIndex,
  (index) => index !== undefined && selectLocation(index)
);

useMap(mapElementRef, {
  onReady: (map) => {
    if (!popupElement.value) return;
    const popupRenderer = new MapPopupRenderer(map, {
      element: popupElement.value,
    });
    const markerLayer = new MapMarkerLayer(map, {
      renderer: () =>
        renderMarkerWithImage(`${staticUrl}/images/icons/work-place.svg`, '', {
          sizeFr: 0.6,
        }),
      onMarkerClick: (marker) => {
        popupLocationIndex.value = 0;
        popupRenderer.setPosition(marker.coords);
      },
    });
    watch(
      () => props.locations,
      (locations) => {
        const markers = locations.map<MapMarker>((location) => ({
          coords: location.location,
        }));
        markerLayer.setMarkers(markers);
        map.extent = markerLayer.getExtent();
      },
      { immediate: true }
    );
    watch(popupLocationIndex, (index) => {
      if (index === undefined) return;
      map.center = props.locations[index].location;
    });
  },
});

function showLightbox() {
  if (!currentLocation.value?.photoUrls.length) return;
  isLightboxVisible.value = true;
}
</script>

<template>
  <div class="partner-map">
    <div class="partner-map__map">
      <div ref="mapElementRef" class="partner-map__map-element"></div>
      <div v-if="currentLocation" class="d-none d-md-flex map-location">
        <PlaceholderableImg
          class="map-location__img rounded-sm"
          :src="currentLocation.photoUrls[0]"
          @click="showLightbox"
        />
        <PartnerLocation :location="currentLocation" />
      </div>
    </div>
    <div class="d-md-none partner-map__location-slider">
      <PartnerLocations v-model="locationIndex" :locations="locations" />
    </div>
    <AppLightbox
      v-model="isLightboxVisible"
      :image-urls="currentLocation?.photoUrls"
    />
  </div>
</template>

<style scoped lang="scss">
.partner-map {
  --map-safe-zone: 20px;

  height: 400px;
  display: flex;
  flex-direction: column;

  &__map {
    position: relative;
    overflow: hidden;
    width: 100%;
    height: 100%;

    @include media-breakpoint-up(sm) {
      border-radius: var(--app-br-lg);
    }
  }

  &__location-slider {
    z-index: 50;
    background: var(--app-color-white);
    border-radius: var(--app-br-lg);
    margin-top: calc(-1 * var(--app-br-lg));
    padding: var(--app-spacing-4);
  }

  &__map-element {
    width: 100%;
    height: 100%;
  }
}

.map-location {
  display: flex;
  flex-direction: column;
  gap: var(--app-spacing-3);
  background: var(--app-color-white);
  position: absolute;
  left: var(--map-safe-zone);
  top: var(--map-safe-zone);
  bottom: var(--map-safe-zone);
  width: 30%;
  margin: 0;
  border-radius: var(--app-br-sm);
  overflow: auto;
  padding: var(--app-spacing-4);

  &__img {
    aspect-ratio: 3 / 2;
    cursor: pointer;
  }
}
</style>
